import type { QueryClient } from "@tanstack/react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryOpts } from "~api/helpers";
import { toolKeys } from "~api/toolKeys.ts";
import type { FilterParams } from "~api/tools.ts";
import { getRelevantSearchText, getTools } from "~api/tools.ts";
import type { OfflinePrefetchOptions } from "~contexts/OfflineContext/OfflinePrefetchOptions.ts";
import {
  type Employee,
  type GeoPosition,
  ToolAdministrationService,
  type ToolForm,
  type ToolHistoryEntry,
  ToolInventoryHistoryService,
  ToolInventoryService,
} from "~generated";
import { getCurrentInventoryYear } from "../inventoryUtils.ts";

export function useInfiniteAllTools(filters: FilterParams, enabled = true) {
  const searchText = getRelevantSearchText(filters);

  return useInfiniteQuery({
    queryKey: toolKeys.infiniteList({ ...filters, searchText }),
    queryFn: async ({ pageParam: page }) =>
      getTools({ ...filters, searchText, page }),
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
    enabled,
  });
}

export function useInfiniteInventory(filters: FilterParams, enabled: boolean) {
  const searchText = getRelevantSearchText(filters);

  return useInfiniteQuery({
    queryKey: toolKeys.infiniteInventory({ ...filters, searchText }),
    queryFn: async ({ pageParam: page }) => {
      const response = await ToolInventoryService.getInventory(
        filters.inventoryYear || getCurrentInventoryYear(),
        page,
        filters.billable,
        filters.categoryId,
        filters.limit,
        searchText,
        filters.serialNumber,
        filters.withoutResponsibleEmployee,
        filters.responsibilityStates,
        filters.responsibleEmployeeIds,
        filters.managerIds,
        filters.tenantIds,
        filters.abbreviations,
        filters.inventoried,
      );
      if (!response?.tools) {
        return {
          tools: [],
          count: 0,
          available_count: 0,
          assigned_count: 0,
          taken_count: 0,
          next_page: null,
        };
      }
      return response;
    },
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
    enabled,
  });
}

export function useToolDetail({ id }: { id?: string }) {
  return useQuery({
    queryKey: toolKeys.detail(id || ""),
    queryFn: async () => ToolAdministrationService.getToolDetail(id || ""),
    enabled: !!id,
  });
}

export function useCreateTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (toolForm: ToolForm) =>
      ToolAdministrationService.createTool(toolForm),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
  });
}

interface UpdateToolArgs {
  id: string;
  toolForm: ToolForm;
  originalRequestTimestamp?: string;
}
export async function updateTool({
  id,
  toolForm,
  originalRequestTimestamp = new Date().toISOString(),
}: UpdateToolArgs) {
  return ToolAdministrationService.updateTool(
    id,
    originalRequestTimestamp,
    toolForm,
  );
}

export function useUpdateTool<TContext>({
  onMutate = () => ({}) as TContext,
}: {
  onMutate?: (
    queryClient: QueryClient,
    updateToolArgs: UpdateToolArgs,
  ) => TContext;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.updateToolMutationKey,
    mutationFn: updateTool,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
  });
}

interface AssignToolArgs {
  id: string;
  responsibleEmployeeId: string;
}
async function assignTool({ id, responsibleEmployeeId }: AssignToolArgs) {
  return ToolAdministrationService.assignTool(id, {
    responsible_employee_id: responsibleEmployeeId,
  });
}
export function useAssignTool<TContext>({
  onMutate = () => ({}) as TContext,
  onSuccess,
}: {
  onMutate?: (
    queryClient: QueryClient,
    assignToolArgs: AssignToolArgs,
  ) => TContext;
  onSuccess?: () => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.assignToolMutationKey,
    mutationFn: assignTool,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSuccess,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    scope: {
      id: "update-tool-responsibility-state",
    },
  });
}

interface RejectToolArgs {
  id: string;
  originalRequestTimestamp?: string;
}
async function rejectTool({
  id,
  originalRequestTimestamp = new Date().toISOString(),
}: RejectToolArgs) {
  return ToolAdministrationService.rejectTool(id, originalRequestTimestamp);
}
export function useRejectTool<TContext>({
  onMutate = () => ({}) as TContext,
}: {
  onMutate?: (
    queryClient: QueryClient,
    rejectToolArgs: RejectToolArgs,
  ) => TContext;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.rejectToolMutationKey,
    mutationFn: rejectTool,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    scope: {
      id: "update-tool-responsibility-state",
    },
  });
}

interface MakeToolAvailableArgs {
  id: string;
}
async function makeToolAvailable({ id }: RejectToolArgs) {
  return ToolAdministrationService.makeToolAvailable(id);
}
export function useMakeToolAvailable<TContext>({
  onMutate = () => ({}) as TContext,
}: {
  onMutate?: (
    queryClient: QueryClient,
    makeToolAvailableArgs: MakeToolAvailableArgs,
  ) => TContext;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.makeToolAvailableMutationKey,
    mutationFn: makeToolAvailable,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    scope: {
      id: "update-tool-responsibility-state",
    },
  });
}

interface TakeToolArgs {
  id: string;
  position: GeoPosition | null;
  originalRequestTimestamp?: string;
}
async function takeTool({
  id,
  position,
  originalRequestTimestamp = new Date().toISOString(),
}: TakeToolArgs) {
  return ToolAdministrationService.takeTool(id, originalRequestTimestamp, {
    position,
  });
}
export function useTakeTool<TContext>({
  onMutate = () => ({}) as TContext,
}: {
  onMutate?: (queryClient: QueryClient, takeToolArgs: TakeToolArgs) => TContext;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.takeToolMutationKey,
    mutationFn: takeTool,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    scope: {
      id: "update-tool-responsibility-state",
    },
  });
}

interface ReturnToolArgs {
  id: string;
  position: GeoPosition | null;
  originalRequestTimestamp?: string;
}
async function returnTool({
  id,
  position,
  originalRequestTimestamp = new Date().toISOString(),
}: ReturnToolArgs) {
  return ToolAdministrationService.returnTool(id, originalRequestTimestamp, {
    position,
  });
}
export function useReturnTool<TContext>({
  onMutate = () => ({}) as TContext,
}: {
  onMutate?: (
    queryClient: QueryClient,
    returnToolArgs: ReturnToolArgs,
  ) => TContext;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: toolKeys.returnToolMutationKey,
    mutationFn: returnTool,
    onMutate: (variables) => onMutate?.(queryClient, variables),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: toolKeys.base() });
    },
    scope: {
      id: "update-tool-responsibility-state",
    },
  });
}

async function getToolsResponsibleEmployees() {
  const employees = await ToolAdministrationService.getResponsibleEmployees();
  if (!employees?.employees) {
    return [];
  }

  return employees.employees;
}

export function useToolsResponsibleEmployees(
  options?: QueryOpts<
    Employee[],
    ReturnType<typeof toolKeys.responsibleEmployees>
  >,
) {
  return useQuery({
    ...options,
    queryKey: toolKeys.responsibleEmployees(),
    queryFn: async () => getToolsResponsibleEmployees(),
  });
}

async function getToolsManagers() {
  const managers = await ToolAdministrationService.getManagers();
  if (!managers?.employees) {
    return [];
  }

  return managers.employees;
}

export function useToolsManagers(
  options?: QueryOpts<Employee[], ReturnType<typeof toolKeys.managers>>,
) {
  return useQuery({
    ...options,
    queryKey: toolKeys.managers(),
    queryFn: () => getToolsManagers(),
  });
}
async function getToolsManufacturers() {
  const response = await ToolAdministrationService.getManufacturers();
  if (!response?.manufacturers) {
    return [];
  }

  return response.manufacturers;
}

export function useToolsManufacturers(
  options?: QueryOpts<string[], ReturnType<typeof toolKeys.manufacturers>>,
) {
  return useQuery({
    ...options,
    queryKey: toolKeys.manufacturers(),
    queryFn: () => getToolsManufacturers(),
  });
}

export function useToolsAdditionalData(options?: OfflinePrefetchOptions) {
  return useQuery({
    ...options,
    queryKey: toolKeys.additionalData(),
    queryFn: () => ToolAdministrationService.getAdditionalData(),
  });
}

async function getToolHistory(id: string) {
  const response = await ToolAdministrationService.getToolHistory(id);
  if (!response?.history) {
    return [];
  }
  return response.history;
}

export function useToolHistory({
  id,
  options,
}: {
  id: string;
  options?: QueryOpts<ToolHistoryEntry[], ReturnType<typeof toolKeys.history>>;
}) {
  return useQuery({
    queryKey: toolKeys.history(id),
    queryFn: () => getToolHistory(id),
    ...options,
  });
}

export function useToolInventoryHistory({ id }: { id: string }) {
  return useQuery({
    queryKey: toolKeys.inventoryHistory(id),
    queryFn: async () => {
      const response =
        await ToolInventoryHistoryService.getInventoryHistory(id);
      if (!response?.history_entries) {
        return [];
      }
      return response.history_entries;
    },
  });
}
